import { createContext, ReactNode, useContext, useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { User as SelectUser } from "@/types/schema";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { supabase } from "@/lib/supabase";

type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  isAdmin: boolean;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, RegisterData>;
  airlineData?: any; // Added to support the billing page
};

type LoginData = {
  username: string;
  password: string;
};

// Registration schema
const registerSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
  fullName: z.string().min(2, "Full name is required"),
  airlineName: z.string().optional(),
  jobTitle: z.string().min(2, "Job title is required"),
  role: z.enum(["admin", "auditor"]),
  airlineId: z.number().nullable().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterData = z.infer<typeof registerSchema>;

// Create an interface for the context store value
interface AuthContextStore {
  readonly current: React.Context<AuthContextType | null>;
}

// Use a module-level variable that won't be affected by Fast Refresh
// This approach is recommended by React team for context compatibility with Fast Refresh
const AuthContextStore: AuthContextStore = {
  current: createContext<AuthContextType | null>(null)
};

// Export a getter function for the context
export function getAuthContext() {
  return AuthContextStore.current;
}

// Fast Refresh compatible component
const SupabaseAuthProviderComponent = memo(function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SelectUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [airlineData, setAirlineData] = useState<any>(null);
  
  // Ref to prevent duplicate auth state change handlers
  const authListenerRef = useRef<any>(null);
  
  // Stable callback functions to prevent unnecessary re-renders
  const setUserCallback = useCallback((user: SelectUser | null) => {
    setUser(user);
    queryClient.setQueryData(["/api/user"], user);
  }, [queryClient]);

  // Memoize the auth state change handler to prevent recreation
  const handleAuthStateChange = useCallback(async (event: any, session: any) => {
    if (event === 'SIGNED_IN' && session) {
      // Use metadata-first approach for faster response
      const userMetadata = session.user.user_metadata;
      
      // Handle both UUID role_id (new) and integer role_id (legacy)
      let role = 'auditor'; // default
      if (typeof userMetadata.role_id === 'string') {
        // UUID role_id - use the role field directly
        role = userMetadata.role || 'auditor';
      } else if (typeof userMetadata.role_id === 'number') {
        // Legacy integer role_id
        role = userMetadata.role_id === 1 ? 'admin' : 'auditor';
      } else if (userMetadata.role) {
        // Fallback to role field
        role = userMetadata.role;
      }
      
      const mappedUser: SelectUser = {
        id: parseInt(session.user.id) || 0,
        username: userMetadata.username || session.user.email || '',
        fullName: userMetadata.full_name || '',
        role: role,
        airlineId: role === 'admin' ? null : userMetadata.organization_id || null,
        airlineName: role === 'admin' ? null : userMetadata.airline_name || null,
        jobTitle: userMetadata.job_title || null,
        password: '',
        authorityProfileId: null,
        isActive: true,
      };
      
      setUserCallback(mappedUser);
      
      // Update with database info in background (non-blocking)
      supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single()
        .then(({ data: dbUser, error: dbError }: { data: any, error: any }) => {
          if (!dbError && dbUser) {
            const updatedUser: SelectUser = {
              ...mappedUser,
              fullName: dbUser.full_name || mappedUser.fullName,
              role: dbUser.role || mappedUser.role,
              airlineId: dbUser.role === 'admin' ? null : dbUser.organization_id || mappedUser.airlineId,
              jobTitle: dbUser.job_title || mappedUser.jobTitle,
              isActive: dbUser.is_active !== false,
            };
            setUserCallback(updatedUser);
            // 🚀 OPTIMIZATION: Update cache with latest data
            localStorage.setItem('aerosync_user', JSON.stringify(updatedUser));
          }
        })
        .catch((error) => {
          // Only log in development to reduce console noise
          if (process.env.NODE_ENV === 'development') {
            console.debug('Optional user profile update failed:', error.message);
          }
        });
    } else if (event === 'SIGNED_OUT') {
      setUser(null);
      queryClient.setQueryData(["/api/user"], null);
    }
  }, [setUserCallback, queryClient]);

  // Check for existing session on mount
  useEffect(() => {
    async function getCurrentUser() {
      try {
        setIsLoading(true);
        
        // 🚀 OPTIMIZATION: Check localStorage first for faster initial load
        const cachedUser = localStorage.getItem('aerosync_user');
        if (cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            // Set cached user immediately for better UX
            setUser(parsedUser);
            setIsLoading(false); // Reduce loading time with cached data
          } catch (e) {
            // Clear invalid cached data
            localStorage.removeItem('aerosync_user');
          }
        }
        
        // Verify with Supabase session (runs in background if cached user exists)
        const { data: session, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          if (process.env.NODE_ENV === 'development') {
            console.error("Session error:", sessionError);
          }
          setError(new Error(sessionError.message));
          setUser(null);
          localStorage.removeItem('aerosync_user'); // Clear invalid cache
          return;
        }
        
        if (!session.session) {
          setUser(null);
          localStorage.removeItem('aerosync_user'); // Clear cache on logout
          return;
        }

        // Get user metadata from session
        const sessionUser = session.session.user;
        if (sessionUser) {
          // Extract user metadata and convert to our User type
          const userMetadata = sessionUser.user_metadata;
          
          // Handle both UUID role_id (new) and integer role_id (legacy)
          let role = 'auditor'; // default
          if (typeof userMetadata.role_id === 'string') {
            // UUID role_id - use the role field directly
            role = userMetadata.role || 'auditor';
          } else if (typeof userMetadata.role_id === 'number') {
            // Legacy integer role_id
            role = userMetadata.role_id === 1 ? 'admin' : 'auditor';
          } else if (userMetadata.role) {
            // Fallback to role field
            role = userMetadata.role;
          }
          
          const mappedUser: SelectUser = {
            id: parseInt(sessionUser.id) || 0,
            username: userMetadata.username || sessionUser.email || '',
            fullName: userMetadata.full_name || '',
            role: role,
            airlineId: role === 'admin' ? null : userMetadata.organization_id || null,
            airlineName: role === 'admin' ? null : userMetadata.airline_name || null,
            jobTitle: userMetadata.job_title || null,
            password: '',
            authorityProfileId: null,
            isActive: true,
          };
          
          setUser(mappedUser);
          // 🚀 OPTIMIZATION: Cache user data for faster subsequent loads
          localStorage.setItem('aerosync_user', JSON.stringify(mappedUser));
          
          // Try to update with database info in background (non-blocking)
          // 🚀 OPTIMIZATION: Reduce timeout for faster response
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 2000) // Reduced from default timeout
          );
          
          Promise.race([
            supabase
              .from('users')
              .select('*')
              .eq('id', sessionUser.id)
              .single(),
            timeoutPromise
          ])
            .then(({ data: dbUser, error: dbError }: { data: any, error: any }) => {
              if (!dbError && dbUser) {
                const updatedUser: SelectUser = {
                  ...mappedUser,
                  fullName: dbUser.full_name || mappedUser.fullName,
                  role: dbUser.role || mappedUser.role,
                  airlineId: dbUser.role === 'admin' ? null : dbUser.organization_id || mappedUser.airlineId,
                  jobTitle: dbUser.job_title || mappedUser.jobTitle,
                  isActive: dbUser.is_active !== false,
                };
                setUserCallback(updatedUser);
                // 🚀 OPTIMIZATION: Update cache with latest data
                localStorage.setItem('aerosync_user', JSON.stringify(updatedUser));
              }
            })
            .catch((error) => {
              // Only log in development to reduce console noise
              if (process.env.NODE_ENV === 'development') {
                console.debug('Optional user profile update failed:', error.message);
              }
            });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError(error as Error);
        setUser(null);
        localStorage.removeItem('aerosync_user'); // Clear cache on error
      } finally {
        setIsLoading(false);
      }
    }
    
    getCurrentUser();
    
    // Set up auth state change listener with stable callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      handleAuthStateChange
    );
    
    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [setUserCallback]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      // Use email/password login with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.username,
        password: credentials.password
      });

      if (error) {
        console.error("Supabase login error:", error);
        throw new Error(error.message);
      }
      
      // The onAuthStateChange listener will handle setting the user state
      // We just need to return something that tanstack-query can resolve
      // Let's create a temporary user object from the response
      if (!data.user) {
        throw new Error("Login failed: no user data returned");
      }
      
      const userMetadata = data.user.user_metadata;
      return {
        id: parseInt(data.user.id) || 0,
        username: userMetadata.username || data.user.email || '',
        fullName: userMetadata.full_name || '',
        role: userMetadata.role || 'auditor',
        airlineId: userMetadata.role === 'admin' ? null : userMetadata.organization_id || null,
        airlineName: userMetadata.role === 'admin' ? null : userMetadata.airline_name || null,
        jobTitle: userMetadata.job_title || null,
        password: '',
        authorityProfileId: null,
        isActive: true,
      };
    },
    onSuccess: (user: SelectUser) => {
      toast({
        title: "Login successful",
        description: `Welcome back, ${user.fullName}!`,
        duration: 4000,
      });
      // The auth listener already sets the user data, but we can also set it here
      // to make the UI update faster
      queryClient.setQueryData(["/api/user"], user);
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message || "Invalid username or password",
        variant: "destructive",
        duration: 4000,
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      const { confirmPassword, ...credentials } = data;
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: credentials.username,
        password: credentials.password,
        options: {
          data: {
            full_name: credentials.fullName,
            job_title: credentials.jobTitle,
            role: credentials.role,
            username: credentials.username,
            airline_name: credentials.airlineName,
            organization_id: credentials.airlineId,
          }
        }
      });
      
      if (signUpError) {
        throw new Error(signUpError.message);
      }
      
      if (!signUpData.user) {
        throw new Error("Registration failed: no user data returned");
      }
      
      const userMetadata = signUpData.user.user_metadata;
      return {
        id: parseInt(signUpData.user.id) || 0,
        username: userMetadata.username || signUpData.user.email || '',
        fullName: userMetadata.full_name || '',
        role: userMetadata.role || 'auditor',
        airlineId: userMetadata.role === 'admin' ? null : userMetadata.organization_id || null,
        airlineName: userMetadata.role === 'admin' ? null : userMetadata.airline_name || null,
        jobTitle: userMetadata.job_title || null,
        password: '',
        authorityProfileId: null,
        isActive: true,
      };
    },
    onSuccess: (user: SelectUser) => {
      toast({
        title: "Registration successful",
        description: `Welcome to AeroSync, ${user.fullName}!`,
        duration: 4000,
      });
      queryClient.setQueryData(["/api/user"], user);
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message || "Could not create account",
        variant: "destructive",
        duration: 4000,
      });
    },
  });

  const logout = async () => {
    try {
      setIsLoading(true);
      await supabase.auth.signOut();
      setUser(null);
      setError(null);
      // 🚀 OPTIMIZATION: Clear cached user data on logout
      localStorage.removeItem('aerosync_user');
    } catch (err) {
      console.error("Error signing out:", err);
      setError(err instanceof Error ? err : new Error('Failed to sign out'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
        duration: 4000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
        duration: 4000,
      });
    },
  });
  
  // Compute isAdmin based on user role
  const isAdmin = useMemo(() => user?.role === 'admin', [user?.role]);
  
  // Memoize the provider value to prevent re-renders on every parent component change
  const value = useMemo(
    () => ({
      user,
      isLoading,
      error,
      isAdmin,
      loginMutation,
      logoutMutation,
      registerMutation,
      airlineData
    }),
    [user, isLoading, error, isAdmin, loginMutation, logoutMutation, registerMutation, airlineData]
  );
  
  return (
    <AuthContextStore.current.Provider value={value}>
      {children}
    </AuthContextStore.current.Provider>
  );
});

// Create a wrapper component
export function AuthProvider({ children }: { children: ReactNode }) {
  return <SupabaseAuthProviderComponent>{children}</SupabaseAuthProviderComponent>;
}

export function useAuth() {
  const context = useContext(AuthContextStore.current);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
